import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronLeft, ChevronRight, X, ArrowLeft, Play, Pause, Volume2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchMapById } from '../services/api';
import SEOHead from './SEOHead';

// Global registry for mobile audio players to prevent simultaneous playback
const mobileAudioRegistry = {
  currentPlaying: null,
  instances: new Map(),
  
  register(id, audioElement, setIsPlaying) {
    this.instances.set(id, { audioElement, setIsPlaying });
  },
  
  unregister(id) {
    this.instances.delete(id);
    if (this.currentPlaying === id) {
      this.currentPlaying = null;
    }
  },
  
  play(id) {
    // Stop all other audio players first
    this.instances.forEach((instance, instanceId) => {
      if (instanceId !== id && instance.audioElement) {
        instance.audioElement.pause();
        instance.audioElement.currentTime = 0;
        instance.setIsPlaying(false);
      }
    });
    
    // Play the requested audio
    const instance = this.instances.get(id);
    if (instance && instance.audioElement) {
      instance.audioElement.play();
      instance.setIsPlaying(true);
      this.currentPlaying = id;
    }
  },
  
  stop(id) {
    const instance = this.instances.get(id);
    if (instance && instance.audioElement) {
      instance.audioElement.pause();
      instance.setIsPlaying(false);
    }
    if (this.currentPlaying === id) {
      this.currentPlaying = null;
    }
  },
  
  stopAll() {
    this.instances.forEach((instance) => {
      if (instance.audioElement) {
        instance.audioElement.pause();
        instance.audioElement.currentTime = 0;
        instance.setIsPlaying(false);
      }
    });
    this.currentPlaying = null;
  }
};

// Local Audio Player Component for 3D Gallery maps (uses local MP3 files)
const LocalAudioPlayer = ({ audioSrc, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const instanceId = useRef(`mobile-audio-${audioSrc}`);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (audioRef.current) {
      if (isPlaying) {
        mobileAudioRegistry.stop(instanceId.current);
      } else {
        mobileAudioRegistry.play(instanceId.current);
      }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    const id = instanceId.current;
    
    if (audio) {
      // Register this audio instance
      mobileAudioRegistry.register(id, audio, setIsPlaying);
      
      const handleEnded = () => {
        setIsPlaying(false);
        mobileAudioRegistry.currentPlaying = null;
      };
      audio.addEventListener('ended', handleEnded);
      
      return () => {
        audio.removeEventListener('ended', handleEnded);
        mobileAudioRegistry.unregister(id);
      };
    }
  }, [audioSrc]);

  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
      style={{
        background: 'linear-gradient(135deg, rgba(198, 167, 94, 0.08) 0%, rgba(198, 167, 94, 0.03) 100%)',
        border: '1px solid rgba(198, 167, 94, 0.15)',
      }}
      onClick={handleToggle}
    >
      <audio ref={audioRef} src={audioSrc} preload="metadata" />
      <button
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all"
        style={{
          background: isPlaying 
            ? 'linear-gradient(135deg, #C6A75E 0%, #A08040 100%)'
            : 'linear-gradient(135deg, rgba(198, 167, 94, 0.2) 0%, rgba(198, 167, 94, 0.1) 100%)',
          border: '1px solid rgba(198, 167, 94, 0.3)',
        }}
      >
        {isPlaying ? (
          <Pause size={16} className="text-black" />
        ) : (
          <Play size={16} className="text-[#C6A75E] ml-0.5" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Volume2 size={14} className="text-[#C6A75E]/60 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wider text-[#C6A75E]/60">
            Rezumat Audio
          </span>
        </div>
        <p className="text-sm truncate mt-0.5 text-[#C6A75E]">
          {title}
        </p>
      </div>
      {isPlaying && (
        <div className="flex items-center gap-0.5 mr-2">
          <div className="w-0.5 h-3 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="w-0.5 h-4 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
          <div className="w-0.5 h-2 bg-[#C6A75E] rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
        </div>
      )}
    </div>
  );
};

const Mobile3DGallery = ({ onMapClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const [splineError, setSplineError] = useState(false);
  const [isComponentReady, setIsComponentReady] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Force dark background IMMEDIATELY on mount - before any render
  useLayoutEffect(() => {
    // Set background on all levels immediately
    document.documentElement.style.backgroundColor = '#1a1a1a';
    document.documentElement.setAttribute('data-page', 'galerie-3d');
    document.body.style.backgroundColor = '#1a1a1a';
    const root = document.getElementById('root');
    if (root) {
      root.style.backgroundColor = '#1a1a1a';
    }
    
    // Mark component as ready after a brief delay to ensure DOM is stable
    const readyTimer = setTimeout(() => {
      setIsComponentReady(true);
    }, 100);
    
    return () => {
      clearTimeout(readyTimer);
      document.documentElement.style.backgroundColor = '';
      document.documentElement.removeAttribute('data-page');
      document.body.style.backgroundColor = '';
      if (root) {
        root.style.backgroundColor = '';
      }
    };
  }, []);

  // Lock page scroll when component mounts
  useEffect(() => {
    // Save current scroll position and lock body
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      // Restore scroll position when unmounting
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Reset spline state when changing slides
  useEffect(() => {
    setSplineLoaded(false);
    setSplineError(false);
  }, [currentIndex]);

  const showcases = [
    {
      id: 'NZ8_8927',
      splineUrl: 'https://my.spline.design/untitled-Ny6XkuQG25Wfmoohq9jvD9af/',
      title: '2 Transsylvania XXI Nova Tabula',
      subtitle: 'Gravură pe cupru - Johannes Sambucus',
      year: '1566',
      author: 'Johannes Sambucus',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/mtbhdbrc_NZ8_8927.jpg',
      description: 'Harta Transilvaniei publicată în anul 1566 la Viena, realizată de savantul umanist Johannes Sambucus, reprezintă o veritabilă capodoperă a cartografiei renascentiste.',
      audioSrc: '/assets/audio/Transsylvania XXI Nova Tabula.mp3',
    },
    {
      id: 'NZ8_8871',
      splineUrl: 'https://my.spline.design/untitled-mZtG5n0fYxl65bXMDA6Yi56Z/',
      title: 'Tabula Europae IX',
      subtitle: 'Geographia Universalis',
      year: '1596',
      author: 'Giovanni Antonio Magini',
      region: 'dacia',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/umd943ci_NZ8_8871.jpg',
      description: 'A noua tablă a Europei din Geografia lui Ptolemeu, publicată de Giovanni Antonio Magini. Reprezintă o proiecție a lumii antice, incluzând Dacia și regiunile carpato-danubiano-pontice.',
      audioSrc: null, // Audio eliminat conform cerințelor
    },
    {
      id: 'Z8L_5030',  // Matches MongoDB ID for TABVLA EVROPAE IX
      splineUrl: 'https://my.spline.design/untitledcopy-zYgqEcp4YlfhPy4kGrDAfYuC/',
      title: 'TABVLA EVROPAE IX',
      subtitle: 'A noua hartă a Europei - Ptolemeu',
      year: '150 d.Hr. (tipărit 1559)',
      author: 'Claudius Ptolemeu (tipărit de Sebastian Münster)',
      region: 'dacia',
      imageUrl: '/maps/Z8L_5030.jpg',
      description: 'A noua tablă a Europei din Geografia lui Ptolemeu, reprezentând spațiul carpato-danubiano-pontic în viziunea geografiei antice.',
      audioSrc: '/assets/audio/Dacia la Sec. II.mp3',
    },
    // NEW 3D Maps - IDs match MongoDB for full descriptions
    {
      id: 'NZ8_8921',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-ipc7uTLYGXu4fH5HEYc0zpnO/',
      title: 'Turcici Imperii Descriptio',
      subtitle: 'Descrierea Imperiului Otoman',
      year: '1570',
      author: 'Abraham Ortelius',
      region: 'imperiul-otoman',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/8t6xj8fu_NZ8_8921.jpg',
      description: 'O hartă detaliată a Imperiului Otoman, prezentând întinderea vastă a teritoriilor controlate de Sublima Poartă în perioada sa de maximă expansiune.',
      audioSrc: null,
    },
    {
      id: 'NZ8_8906',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-hVbuSVJsmo54o4DNkjcLYDdA/',
      title: 'Transsylvania XXI Nova Tabula (Ed. II)',
      subtitle: 'Ediția a Doua - Cosmographia',
      year: '1544',
      author: 'Sebastian Münster',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/d016wt2a_NZ8_8906.jpg',
      description: 'O reprezentare cartografică detaliată a Transilvaniei din perioada Renașterii.',
      audioSrc: '/assets/audio/Transsylvania XXI Nova Tabula (Ed. II).mp3',
    },
    {
      id: 'NZ8_8950',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-KvKIUoPLEURXNlwSfoKnpcl6/',
      title: 'Nova Tabula Transylvaniae',
      subtitle: 'Noua Hartă a Transilvaniei',
      year: '1640',
      author: 'Willem & Joan Blaeu',
      region: 'transilvania',
      imageUrl: '/maps/NZ8_8950.jpg',
      description: 'Noua Hartă a Transilvaniei realizată de celebra casă cartografică olandeză Blaeu, oferind o perspectivă detaliată asupra regiunii.',
      audioSrc: null,
    },
    {
      id: 'NZ8_8880',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-RKCPMAqeMoshjHruDMDKTai4/',
      title: 'TRANSYLVANIA',
      subtitle: 'Hartă Istorică',
      year: '1630',
      author: 'Gerard Mercator / Jodocus Hondius',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/pbiwmrgb_NZ8_8880.jpg',
      description: 'O reprezentare clasică a Transilvaniei, evidențiind granițele naturale formate de lanțul munților Carpați și principalele văi.',
      audioSrc: '/assets/audio/TRANSYLVANIA.mp3',
    },
    {
      // Exemplarul vechi a fost donat ca premiu la Festival. Scena Spline a fost
      // republicata de proprietar cu exemplarul nou, pe aceeasi adresa, deci cardul
      // e inapoi in galeria 3D. Textura sta in scene.splinecode, pe serverele Spline,
      // nu in repo: daca mai apare vreodata exemplarul vechi, se republica scena.
      id: 'Z8L_5079',  // Matches MongoDB ID - Mercator map
      splineUrl: 'https://my.spline.design/untitled-7MMBTJTUdslCZD5b6KbOLOUz/',
      title: 'VALACHIA, SERVIA, BULGARIA, VND ROMANIA',
      subtitle: 'Valahia, Serbia, Bulgaria și Rumelia - Gerardus Mercator',
      year: '1589',
      author: 'Gerardus Mercator',
      region: 'valahia',
      // Rezerva afisata cand splineError e true (linia ~608). Trebuie sa fie
      // exemplarul NOU, nu cel donat.
      imageUrl: '/maps/Z8L_5079.jpg',
      description: 'O hartă cuprinzătoare a regiunilor balcanice, prezentând Valahia, Serbia, Bulgaria și teritoriile românești sub dominație otomană.',
      audioSrc: '/assets/audio/WALACHIA SERVIA BULGARIA - Homann.mp3',
    },
    {
      id: 'NZ8_8945',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-8CFuSfsCfQo0qkN7ff6BIXGf/',
      title: 'Hungarie Descriptio',
      subtitle: 'Descrierea Ungariei',
      year: '1570',
      author: 'Wolfgang Lazius / Ortelius',
      region: 'ungaria',
      imageUrl: 'https://customer-assets.emergentagent.com/job_f71459f5-332b-488f-a9b0-69ae068f31ee/artifacts/lz5l9ys4_NZ8_8945.jpg',
      description: 'O hartă detaliată a Regatului Ungariei, incluzând teritoriile transilvanene și regiunile învecinate în perioada medievală târzie.',
      audioSrc: null,
    },
    {
      id: 'Z8W_9191',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-Mi2gSiZg7cuhiijL3dHNHZt3/',
      title: 'Transylvania - Hartă Mercator',
      subtitle: 'Transilvania în viziunea lui Mercator',
      year: '1595',
      author: 'Gerardus Mercator',
      region: 'transilvania',
      imageUrl: 'https://customer-assets.emergentagent.com/job_485114d5-d432-4824-9dce-6f422fc29e4f/artifacts/wl5hmqn0_Z8W_9191.jpg.jpeg',
      description: 'Harta Transilvaniei realizată de celebrul cartograf Gerardus Mercator.',
      audioSrc: '/assets/audio/Transylvania - Hartă Mercator.mp3',
    },
    {
      id: 'munster_cosmographia_1550',  // Matches MongoDB ID
      splineUrl: 'https://my.spline.design/untitled-VIWm4HGSaqLEpPA7yaCfJjwy/',
      title: 'Von Transsylvania, Siebenbürgen',
      subtitle: 'Cosmographia - Sebastian Münster',
      year: '1550',
      author: 'Sebastian Münster',
      region: 'valahia-moldova-transilvania',
      imageUrl: '/maps/munster_cosmographia_1550.jpg',
      description: 'Harta xilogravată din Cosmographia lui Sebastian Münster ilustrează relieful carpatic și așezările din Transilvania.',
      audioSrc: null,
    },
    {
      id: 'ORTELIUS_DACIARUM_1595_OL',
      splineUrl: 'https://my.spline.design/untitled-1pZpKGxLNvmfERinK6prcJsZ/',
      title: 'Tabula Nova Europae Orientalis et Meridionalis',
      subtitle: 'Spațiul Daco-Moesic în Antichitate - Waldseemüller, 1525',
      year: '1525',
      author: 'Waldseemüller',
      region: 'dacia',
      imageUrl: '/maps/daciarum-moesiarumque-ortelius.jpeg',
      description: 'Ediție timpurie a hărții Daciei și Moesiei, realizată de Abraham Ortelius și revizuită de Jodocus Hondius în 1595. Reprezintă spațiul daco-moesic în Antichitate, cu o coloratură caracteristică ediției olandeze, evidențiind rețeaua hidrografică și principalele cetăți antice.',
      audioSrc: null,
    },
    {
      id: 'ORTELIUS_DACIARUM_1603',
      splineUrl: 'https://my.spline.design/untitled-eyKKnVryG74TfInHD5U0J40b/',
      title: 'Daciarum, Moesiarumque, Vetus Descriptio 2',
      subtitle: 'Dacia și Moesia - Abraham Ortelius, Parergon 1603',
      year: '1603',
      author: 'Abraham Ortelius',
      region: 'dacia',
      imageUrl: '/maps/daciarum-moesiarumque-vetus.jpg',
      description: 'Harta Daciarum, Moesiarumque, Vetus Descriptio este o capodoperă a cartografiei Renașterii, realizată de Abraham Ortelius și publicată în faimosul atlas historic Parergon. Reprezintă Dacia și Moesia în viziunea Antichității, reconstituite pe baza surselor clasice.',
      audioSrc: null,
    },
    {
      id: 'VISSCHER_APOSTELEN_1660',
      splineUrl: 'https://my.spline.design/untitled-5i07QL6Mn5ZY3BzqB4ymmnVm/',
      title: 'Geographische Beschryvinghe van de Wandeling der Apostelen',
      subtitle: 'Călătoriile Apostolilor — Nicolaes Visscher I, Amsterdam c. 1660',
      year: '1660',
      author: 'Nicolaes Visscher I',
      region: 'terra-sancta',
      imageUrl: '/maps/114027.jpg',
      description: 'Lucrarea cartografică a lui Nicolaes Visscher I documentează vizual călătoriile misionare ale Apostolului Pavel prin bazinul estic al Mării Mediterane. Cele opt viniete decorative narează momente cruciale — convertirea pe drumul Damascului, naufragiul de la Malta, înfățișarea înaintea lui Nero — transformând harta într-un tratat teo-geografic unic.',
      audioSrc: '/audio/visscher-apostelen-1660.mp3',
    },
    {
      id: 'ORTELIUS_ROMANIAE_1588',
      splineUrl: 'https://my.spline.design/untitled-iLxgPWabJGJK8X19L6J4yMu6/',
      title: 'Romaniae, (quae olim Thracia dicta) Vicinarumque Regionum Descriptio',
      subtitle: 'Spațiul Balcanic și Românesc — Abraham Ortelius / Gastaldi, 1588',
      year: '1588',
      author: 'Abraham Ortelius / Giacomo Gastaldi',
      region: 'europa',
      imageUrl: '/maps/romaniae-quae-olim-thracia.jpg',
      description: 'Harta lui Abraham Ortelius bazată pe Giacomo Gastaldi reprezintă spațiul balcanic și românesc la sfârșitul secolului al XVI-lea, incluzând Thracia, Bulgaria, Valahia și Serbia. Document fundamental al cartografiei românești, fixează pentru prima dată cu precizie granițele Valahiei pe o hartă de autor european.',
      audioSrc: '/audio/ORTELIUS Romaniae, (quae olim Thracia dicta) Vicinarumque Regionum, Uti Bulgariae, Walachiae, Syrfiae AUDIO.mp3',
    },
    {
      id: 'MERCATOR_TAURICA_1595',
      splineUrl: 'https://my.spline.design/untitled-55468zgVBJM0adBo8uKE8C5k/',
      title: 'Taurica Chersonesus Nostra aetate Przecopsca et Gazara dicitur',
      subtitle: 'Hanatul Crimeei și Stepa Pontică în Cartografia Mercatoriană, 1595',
      year: '1595',
      author: 'Gerardus Mercator / Rumold Mercator',
      region: 'marea-neagra',
      imageUrl: '/maps/MERCATOR_TAURICA_1595.jpg',
      description: 'Capodoperă a cartografiei renascentiste târzii, harta Tauricei Chersonesos publicată postum de Rumold Mercator în 1595 înfățișează Hanatul Crimeei și stepa pontică la apogeul puterii otomano-tătare.',
      audioSrc: '/audio/Taurica Chersonesus.mp3',
    },
    {
      id: 'MUNSTER_CONSTANTINOPEL_1550',
      splineUrl: 'https://my.spline.design/untitled-wb8jvmLGSP30xIXf5pmykj1n/',
      title: 'Constantinopel — Vedere Panoramică din Cosmographia',
      subtitle: 'Întemeierea Noii Rome - Sebastian Münster, Basel',
      year: '1550-1578',
      author: 'Sebastian Münster',
      region: 'imperiu-otoman',
      imageUrl: '/maps/MUNSTER_CONSTANTINOPEL_1550.jpg',
      description: 'Vedere panoramică a Constantinopolului, xilogravură colorată manual din Cosmographia lui Sebastian Münster. Foaia însoțește biografiile împăraților Constantin, Constantius și Iulian și povestește întemeierea Noii Rome pe locul vechiului Byzantium.',
      audioSrc: '/audio/Constantinopel - Cosmographia Munster.mp3',
    },
  ];

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentIndex < showcases.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < showcases.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleViewDetails = async (showcase) => {
    try {
      // Fetch full map data from API for complete description
      const fullMapData = await fetchMapById(showcase.id);
      // Use onMapClick prop to open modal directly
      if (onMapClick) {
        onMapClick(fullMapData);
      }
    } catch (error) {
      console.error('Error fetching map details:', error);
      // Fallback to local data if API fails
      if (onMapClick) {
        onMapClick({
          id: showcase.id,
          title: showcase.title,
          year: showcase.year,
          author: showcase.author,
          region: showcase.region,
          imageUrl: showcase.imageUrl,
          description: showcase.description,
        });
      }
    }
  };

  const currentShowcase = showcases[currentIndex];

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        backgroundColor: '#1a1a1a',
        overflow: 'hidden'
      }}
      data-testid="mobile-3d-gallery"
    >
      <SEOHead
        title="Galerie 3D Hărți Istorice — Casa Cartarum"
        description="Vizualizează hărți istorice ale României în format 3D interactiv. Zoom, rotire și detalii pentru fiecare hartă din colecția Casa Cartarum."
        canonical="https://www.casacartarum.ro/galerie-3d"
      />
      {/* Header with Exit Button */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#C6A75E]/20">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#C6A75E] hover:text-[#d4c090] transition-colors bg-[#2a2a2a] px-4 py-2 rounded-full"
          data-testid="exit-3d-gallery-btn"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Ieșire</span>
        </button>
        <h1 
          className="text-lg font-semibold tracking-wide"
          style={{ 
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: '#C6A75E',
          }}
        >
          Galerie 3D
        </h1>
        <button 
          onClick={() => navigate('/')}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2a2a2a] text-[#C6A75E] hover:bg-[#3a3a3a] transition-colors"
          data-testid="close-3d-gallery-btn"
        >
          <X size={20} />
        </button>
      </div>

      {/* Carousel Indicators */}
      <div className="flex justify-center gap-2 py-3">
        {showcases.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex 
                ? 'bg-[#C6A75E] w-6' 
                : 'bg-[#C6A75E]/30'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Spline Embed Container - LARGER */}
      <div 
        ref={containerRef}
        className="relative flex-1 overflow-hidden"
        style={{ 
          minHeight: '40vh', 
          flex: '1 1 0%',
          touchAction: 'none',
          overscrollBehavior: 'none',
          backgroundColor: '#1a1a1a',
          marginBottom: '-1px'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Spline hint - only show when content is ready */}
        {isComponentReady && (
          <div className="absolute top-3 left-3 right-3 z-10 pointer-events-none">
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
              style={{
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                color: '#C6A75E',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 4h6v6M10 20H4v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
              <span>Rotește și mărește harta 3D</span>
            </div>
          </div>
        )}

        {/* Initial loading state - shown until component is ready */}
        {!isComponentReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] z-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C6A75E]"></div>
              <span className="text-[#C6A75E] text-sm" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                Se încarcă galeria...
              </span>
            </div>
          </div>
        )}

        {/* Loading indicator - for Spline iframe loading (only when component is ready) */}
        {isComponentReady && !splineLoaded && !splineError && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] z-5">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C6A75E]"></div>
          </div>
        )}

        {/* Static image fallback ONLY for errors */}
        {isComponentReady && splineError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1a1a1a]">
            <img 
              src={currentShowcase.imageUrl} 
              alt={currentShowcase.title}
              loading="eager"
              decoding="async"
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}

        {/* Spline iframe - always render when component is ready */}
        {isComponentReady && !splineError && (
          <iframe
            key={currentShowcase.id}
            src={currentShowcase.splineUrl}
            frameBorder="0"
            className="absolute inset-0 w-full h-full"
            style={{ 
              touchAction: 'none',
              opacity: splineLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease',
              backgroundColor: '#1a1a1a',
              border: 'none',
              margin: 0,
              padding: 0,
              display: 'block'
            }}
            title={`3D Historical Map - ${currentShowcase.title}`}
            allow="autoplay; fullscreen; xr-spatial-tracking"
            loading="lazy"
            onLoad={() => setSplineLoaded(true)}
            onError={() => setSplineError(true)}
          />
        )}

        {/* Navigation Arrows */}
        <button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className={`absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            currentIndex === 0 
              ? 'opacity-30 cursor-not-allowed' 
              : 'opacity-80 hover:opacity-100'
          }`}
          style={{
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <ChevronLeft size={24} className="text-[#C6A75E]" />
        </button>

        <button
          onClick={handleNext}
          disabled={currentIndex === showcases.length - 1}
          className={`absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            currentIndex === showcases.length - 1 
              ? 'opacity-30 cursor-not-allowed' 
              : 'opacity-80 hover:opacity-100'
          }`}
          style={{
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <ChevronRight size={24} className="text-[#C6A75E]" />
        </button>

        {/* Cover pentru gap-uri potențiale din iframe Spline */}
        <div
          className="absolute bottom-0 left-0 right-0 h-2 z-10 pointer-events-none"
          style={{ backgroundColor: '#1a1a1a' }}
        />
      </div>

      {/* Info Panel - Compact */}
      <div 
        className="px-4 py-3 flex-shrink-0" 
        style={{ 
          backgroundColor: '#1a1a1a',
          borderTop: 'none',
          marginTop: '0'
        }}
      >
        {/* Small decorative header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="h-[1px] w-6 bg-[#C6A75E]/40" />
          <div className="w-1.5 h-1.5 rotate-45 border border-[#C6A75E]/40" />
          <div className="h-[1px] w-6 bg-[#C6A75E]/40" />
        </div>

        {/* Title */}
        <h2 
          className="text-xl font-semibold mb-1 tracking-wide"
          style={{ 
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            color: '#C6A75E',
          }}
        >
          {currentShowcase.title}
        </h2>

        {/* Subtitle */}
        {currentShowcase.subtitle && (
          <p 
            className="text-sm mb-2"
            style={{ 
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              color: '#A89060',
              fontStyle: 'italic',
            }}
          >
            {currentShowcase.subtitle}
          </p>
        )}

        {/* Year and Author */}
        <div className="flex items-center gap-4 mb-3 text-sm">
          <span className="text-[#C6A75E]">{currentShowcase.year}</span>
          <span className="text-[#6B6B6B]">•</span>
          <span className="text-[#B8A88A] italic">{currentShowcase.author}</span>
        </div>

        {/* Local Audio Player - show if map has audioSrc assigned */}
        {currentShowcase.audioSrc && (
          <div className="mb-4">
            <LocalAudioPlayer audioSrc={currentShowcase.audioSrc} title={currentShowcase.title} />
          </div>
        )}

        {/* Description */}
        <p 
          className="text-sm leading-relaxed mb-3"
          style={{ 
            fontFamily: '"EB Garamond", Georgia, serif',
            color: '#D0D0D0',
          }}
        >
          {currentShowcase.description}
        </p>

        {/* CTA Button */}
        <button
          onClick={() => handleViewDetails(currentShowcase)}
          className="w-full py-2.5 rounded transition-all"
          style={{
            background: 'linear-gradient(135deg, #3A1818 0%, #4A1E1E 50%, #3A1818 100%)',
            border: '1px solid rgba(198, 167, 94, 0.3)',
          }}
        >
          <span 
            className="tracking-wider uppercase text-sm"
            style={{ 
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              color: '#C6A75E',
              fontWeight: 600,
            }}
          >
            Vezi Detalii Complete
          </span>
        </button>

        {/* Slide counter */}
        <p className="text-center text-xs text-[#6B6B6B] mt-2 pb-2">
          {currentIndex + 1} / {showcases.length}
        </p>
      </div>
    </div>
  );
};

export default Mobile3DGallery;
